/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook used to retrieve the Theme Manager Context.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to get access to react base hooks inside custom hook.
 */
import { useEffect } from 'react';

/**
 * Allows for the dispatching of actions to the redux store.
 */
import { useDispatch } from 'react-redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of grade data loaded from the ServiceStack API.
 */
import { SubtermsAndGradesSelectors, useSubtermsAndGradesSelector, SubtermsAndGradesActions } from 'src/store/reducers/subtermsAndGrades';

/**
 * Typings
 */
import { IRequestState } from 'src/store/reducers';

/**
 * Typings
 */
import * as Dtos from '../api/dtos';

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const useSubtermsAndGrades = (
    termId: number
): [
    Dtos.Version | null,
    Dtos.Category | null,
    Dtos.Term | null,
    Dtos.Term | null,
    number[],
    number[],
    IRequestState
] => {
    const version = useSubtermsAndGradesSelector(state => SubtermsAndGradesSelectors.selectVersion(state, termId));
    const category = useSubtermsAndGradesSelector(state => SubtermsAndGradesSelectors.selectCategory(state, termId));
    const term = useSubtermsAndGradesSelector(state => SubtermsAndGradesSelectors.selectTerm(state, termId));
    const parentTerm = useSubtermsAndGradesSelector(state => SubtermsAndGradesSelectors.selectParentTerm(state, termId));
    const termsView = useSubtermsAndGradesSelector(state => SubtermsAndGradesSelectors.selectTermsView(state, termId));
    const gradesView = useSubtermsAndGradesSelector(state => SubtermsAndGradesSelectors.selectGradesView(state, termId));
    const loadState = useSubtermsAndGradesSelector(state => SubtermsAndGradesSelectors.selectLoadState(state, termId));

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(SubtermsAndGradesActions.loadByTermId(termId));

        return () => {
            dispatch(SubtermsAndGradesActions.clear(termId));
        }
    }, [termId, dispatch]);

    return [
        version,
        category,
        parentTerm,
        term,
        termsView,
        gradesView,
        loadState
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default useSubtermsAndGrades;


// WEBPACK FOOTER //
// ./src/hooks/useSubtermsAndGrades.ts